/**
 * Andys site menu (issue #1469).
 *
 * Лендинг становится мобильной главной Andys, поэтому ему нужна навигация по
 * остальным страницам сайта. Список — главное меню andys-motors.com (один
 * Bootstrap-navbar, подменю нет) БЕЗ «Home»: главная — это сам лендинг.
 *
 * `path` — путь на проде, как в навбаре сайта (со слешем на конце там, где он
 * есть у них: без него MODX отвечает 301). `urlKey` — какой origin-aware геттер
 * из runtimeConfig уже знает адрес этой страницы; `pageId` — как страница
 * называется в `WEBSITE_PAGES` widget-config (на проде там `about` / `contact`,
 * а не `about-us` / `contact-us`).
 */
import { composeDealerPageUrl } from '../../../config/dealerUrl';

/** Прод-сайт — фолбэк, когда сайт дилера не резолвится (standalone /andys preview). */
export const ANDYS_SITE_URL = 'https://andys-motors.com';

export const ANDYS_MENU_ITEMS = [
  { id: 'used-cars', label: 'Used Cars', path: '/used-cars', urlKey: 'catalogUrl' },
  { id: 'car-rental', label: 'Car Rental', path: '/car-rental', urlKey: 'rentalUrl' },
  { id: 'leasing', label: 'Leasing', path: '/leasing/', urlKey: 'leasingUrl' },
  { id: 'british-forces', label: 'British Forces', path: '/british-forces/', pageId: /^british-forces$/i },
  { id: 'about-us', label: 'About Us', path: '/about-us', pageId: /^about(-us)?$/i },
  { id: 'contact-us', label: 'Contact Us', path: '/contact-us', pageId: /^contact(-us)?$/i },
];

// composeDealerPageUrl срезает слеш на конце; возвращаем его, если он есть в
// исходном пути, — иначе каждая ссылка меню платит лишний 301 у MODX.
const keepTrailingSlash = (url, path) =>
  url && path.endsWith('/') && !url.endsWith('/') ? `${url}/` : url;

// Сайт дилера + префикс + путь страницы `pageId` из `WEBSITE_PAGES` (или
// дефолтный `path`). null без резолвнутого сайта.
function dealerPageHref({ siteUrl, pathPrefix, pages, pageId, path }) {
  if (!siteUrl) return null;
  const page = pageId ? (pages || []).find((p) => pageId.test(p.id || '')) : null;
  const pagePath = page?.path || path;
  return keepTrailingSlash(composeDealerPageUrl(siteUrl, pathPrefix, pagePath), pagePath);
}

/**
 * Пункты меню с href'ами для текущего окружения: test-лендинг → test-сайт,
 * prod → prod (тот же принцип, что у Rent/Lease CTA, #1252/#1256).
 *
 * Порядок резолва для каждого пункта:
 *   1. готовый origin-aware URL из геттера runtimeConfig (catalog / rental /
 *      leasing) — тот же, по которому уже уводят CTA лендинга;
 *   2. сайт дилера + префикс + путь страницы из `WEBSITE_PAGES` (или дефолтный
 *      путь пункта, если страницы там нет) — через `composeDealerPageUrl`;
 *   3. абсолютный URL прод-сайта.
 *
 * Чистая функция без window/config — контейнер передаёт значения геттеров.
 *
 * @param {object} [sources]
 * @param {string|null} [sources.siteUrl]    config.DEALER_SITE_URL
 * @param {string} [sources.pathPrefix]      config.WEBSITE_PATH_PREFIX
 * @param {Array<{id?: string, path?: string}>} [sources.pages] config.WEBSITE_PAGES
 * @param {string|null} [sources.catalogUrl] config.DEALER_CATALOG_URL
 * @param {string|null} [sources.rentalUrl]  config.DEALER_RENTAL_URL
 * @param {string|null} [sources.leasingUrl] config.DEALER_LEASING_URL
 * @returns {Array<{id: string, label: string, href: string}>}
 */
export function buildAndysMenuItems({
  siteUrl = null,
  pathPrefix = '',
  pages = [],
  catalogUrl = null,
  rentalUrl = null,
  leasingUrl = null,
} = {}) {
  const resolved = { catalogUrl, rentalUrl, leasingUrl };

  return ANDYS_MENU_ITEMS.map(({ id, label, path, urlKey, pageId }) => {
    const href =
      (urlKey ? resolved[urlKey] : null) ||
      dealerPageHref({ siteUrl, pathPrefix, pages, pageId, path });
    return { id, label, href: href || `${ANDYS_SITE_URL}${path}` };
  });
}

/** Страница duty-free машин для British Forces (баннер BFC на Buy-табе). */
export const ANDYS_BFC_PATH = '/british-forces-cars';

/**
 * Ссылка баннера BFC — по тому же принципу, что меню: страница `duty-free` из
 * `WEBSITE_PAGES` на сайте дилера текущего окружения (test → test), без
 * резолвнутого сайта — прод. Раньше была захардкожена на прод, и тестовый
 * лендинг уводил с тестового сайта (#1469).
 *
 * @param {object} [sources]
 * @param {string|null} [sources.siteUrl]  config.DEALER_SITE_URL
 * @param {string} [sources.pathPrefix]    config.WEBSITE_PATH_PREFIX
 * @param {Array<{id?: string, path?: string}>} [sources.pages] config.WEBSITE_PAGES
 * @returns {string}
 */
export function buildAndysBfcUrl({ siteUrl = null, pathPrefix = '', pages = [] } = {}) {
  return (
    dealerPageHref({ siteUrl, pathPrefix, pages, pageId: /^duty-free$/i, path: ANDYS_BFC_PATH }) ||
    `${ANDYS_SITE_URL}${ANDYS_BFC_PATH}`
  );
}

/** Меню без резолвнутого сайта — абсолютные прод-URL (Storybook, preview). */
export const ANDYS_DEFAULT_MENU_ITEMS = buildAndysMenuItems();

export default ANDYS_MENU_ITEMS;
